import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import DatePicker from 'react-datepicker';
import { Modal, Button, Form } from 'react-bootstrap';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../axios.jsx';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

export default function SalaDeAnalisis() {
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('day');
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const user = sessionStorage.getItem('username') || '';
  const nivel = sessionStorage.getItem('usernivel') || '';

  const [form, setForm] = useState({
    Hora_inicio: '',
    Hora_fin: '',
    Tema: '',
    Gestor: user
  });

  const minTime = new Date(); minTime.setHours(6,0,0);
  const maxTime = new Date(); maxTime.setHours(23,0,0);

  // ¿Estamos editando una reserva que ya existe (activa) o creando una nueva?
  const esReservaActiva = !!selected?.resource?.id;

  // Carga eventos por fecha
  useEffect(() => {
    const iso = date.toISOString().split('T')[0];
    api.get(`citar_sar/fecha/${iso}`)
      .then(res => {
        setEvents(res.data.map(c => ({
          id: c.id,
          title: c.Titulo,
          start: new Date(`${c.Fecha}T${c.Hora_inicio}`),
          end: new Date(`${c.Fecha}T${c.Hora_fin}`),
          resource: c
        })));
      })
      .catch(console.error);
    setForm(f => ({ ...f, Gestor: user }));
  }, [date, user]);

  // Resetea form al cerrar modal
  useEffect(() => {
    if (!showModal) {
      setForm({ Hora_inicio: '', Hora_fin: '', Tema: '', Gestor: user });
      setSelected(null);
    }
  }, [showModal, user]);

  const openModal = slot => {
    setSelected(slot);
    const { start, end, resource } = slot;
    setForm({
      Hora_inicio: format(start, 'HH:mm'),
      Hora_fin: format(end, 'HH:mm'),
      Tema: resource?.Titulo || '',
      Gestor: resource?.Responsable || user
    });
    setShowModal(true);
  };

  // Crear o editar
  const handleSave = () => {
    const iso = date.toISOString().split('T')[0];
    const payload = {
      Fecha: iso,
      Hora_inicio: form.Hora_inicio,
      Hora_fin: form.Hora_fin,
      Codigo: 'Sin codigo',
      Titulo: form.Tema,
      Responsable: form.Gestor
    };

    const apiCall = selected?.resource?.id
      ? api.put(`agenda/${selected.resource.id}`, payload)
      : api.post('agenda/', payload);

    apiCall
      .then(() => window.location.reload())
      .catch(err => alert(err.response?.data?.message || 'Error'));
  };

  const handleDelete = () => {
    if (!selected?.resource?.id) return;
    api.delete(`agenda/${selected.resource.id}`)
      .then(() => window.location.reload())
      .catch(err => alert(err.response?.data?.message || 'Error al eliminar'));
  };

  // Mismo esquema de color "activo" (verde) / "pendiente" (gris-azul) usado en CompShowalertas,
  // aplicado a cada evento ya reservado en el calendario.
  const eventPropGetter = () => ({
    style: {
      backgroundColor: '#e6f6ec',
      color: '#1e7e42',
      border: '1px solid #b8e6c6',
      borderRadius: '8px',
      fontWeight: 600,
    },
  });

  return (
    <div style={{ padding: 20 }} className="sala-container">
      {/* Mismo lenguaje visual (pills, tarjeta) que CompShowalertas/ViewSARS/ViewERI */}
      <style>{`
        .sala-container h2 {
          font-weight: 700;
          color: #1f3a5f;
        }

        .sala-card {
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 2px 16px rgba(0,0,0,0.08);
          padding: 1.25rem;
          margin-top: 1rem;
        }

        .sala-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.02em;
        }

        .sala-pill.activa {
          background: #e6f6ec;
          color: #1e7e42;
        }

        .sala-pill.nueva {
          background: #f1f3f5;
          color: #8a94a6;
        }

        .modal-header.sala-header {
          background: #1f3a5f;
          color: #fff;
        }
        .modal-header.sala-header .btn-close {
          filter: invert(1) brightness(2);
        }

        .rbc-event {
          transition: filter 0.15s ease-in-out;
        }
        .rbc-event:hover {
          filter: brightness(0.95);
        }
      `}</style>

      <h2>Calendario de sala SARS</h2>
      <label>Seleccionar fecha: </label>
      <DatePicker selected={date} onChange={setDate} />

      <div className="sala-card">
        <div style={{ height: '60vh' }}>
          <Calendar
            localizer={localizer}
            events={events}
            date={date}
            view={view}
            onNavigate={setDate}
            onView={setView}
            defaultView="day"
            step={30} timeslots={2}
            selectable
            onSelectEvent={openModal}
            onSelectSlot={openModal}
            min={minTime} max={maxTime}
            scrollToTime={minTime}
            eventPropGetter={eventPropGetter}
          />
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton className="sala-header">
          <Modal.Title>Reservar / Editar Sala (SARS)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Mismo indicador de estado que "Activa"/"Sin SARS" en CompShowalertas */}
          <div className="mb-3">
            {esReservaActiva ? (
              <span className="sala-pill activa">
                <i className="fa-solid fa-circle-check"></i> Reserva activa
              </span>
            ) : (
              <span className="sala-pill nueva">
                <i className="fa-regular fa-circle"></i> Nueva reserva
              </span>
            )}
          </div>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Hora inicio</Form.Label>
              <Form.Control type="text" value={form.Hora_inicio}
              onChange={e => setForm({ ...form, Hora_inicio: e.target.value })}  />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Hora fin</Form.Label>
              <Form.Control type="text" value={form.Hora_fin} 
              onChange={e => setForm({ ...form, Hora_fin: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tema</Form.Label>
              <Form.Control
                type="text"
                value={form.Tema}
                onChange={e => setForm({ ...form, Tema: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Responsable</Form.Label>
              <Form.Control value={form.Gestor} readOnly />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
          {nivel === '1' && selected?.resource?.id && (
            <Button variant="danger" onClick={handleDelete}>Eliminar</Button>
          )}
          <Button
            variant={esReservaActiva ? 'success' : 'primary'}
            onClick={handleSave}
            disabled={!form.Tema}
          >
            {esReservaActiva ? 'Guardar cambios' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}